import React, {
    SyntheticEvent,
    useCallback,
    useRef,
    useState,
    useEffect,
} from 'react';

import {
    Button,
    FileDropZone,
    IconInfoCircle,
    IconMusicFile,
    IconUploadCloud,
    TabPanel,
} from '@trqk/components';
import {
    constants,
    locale,
    getFileInputAccept,
    getGapDateRangesFromPeriodSelection,
} from '@trqk/utils';

import { useToast, useUploadDetections } from '../../../../../../../hooks';
import { StepWrapper } from '../../..';
import { BMATUploadInstructions, TuneSatUploadInstructions } from '.';
import { tabKeys } from '../constants';

const { SUPPORTED_DETECTION_FILE_TYPES, MAX_DETECTION_UPLOAD_BATCH_SIZE } =
    constants;

const DropZoneContent = (): JSX.Element => (
    <div className="flex flex-col items-center">
        <IconUploadCloud fillColor="white" width={80} />
        <div className="text-white font-bold font-lg">
            {
                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.UPLOAD_DETECTIONS
                    .DROP_TO_UPLOAD
            }
        </div>
    </div>
);

const fileInputAccept = getFileInputAccept(SUPPORTED_DETECTION_FILE_TYPES);

/**
 * Allows the user to add a detection file for upload.
 */
export const AddDetectionUpload: GapAnalysisStepComponent = props => {
    const { context, remove, send } = props;
    const { selectedPeriod, trqkGap } = context;
    const { add: addToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { handleFiles } = useUploadDetections();

    const addFiles = (): void => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        // When mounting this component, reset uploadedDetectionFile to null so that it does not
        // persist stale information if a user uploads multiple files in succession.
        send({
            type: 'SET_UPLOADED_DETECTION_FILE',
            value: {
                uploadedDetectionFile: null,
            },
        });
    }, []);

    const gapAnalysisId = trqkGap?.gap_analysis_id ?? null;

    /**
     * Check if user is trying to upload more than one file. If so, show a warning toast and do not
     * proceed. Otherwise, proceed with upload.
     * @param {File[]} files the files that were selected for upload
     */
    const uploadOrRejectFiles = useCallback(
        (files: File[]): void => {
            const tooManyFiles = files.length > MAX_DETECTION_UPLOAD_BATCH_SIZE;

            if (tooManyFiles) {
                addToast({
                    content:
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.UPLOAD_DETECTIONS
                            .WARNINGS.ONE_FILE_AT_A_TIME,
                    kind: 'warning-banner',
                });
            } else {
                const onGroupStopped = (
                    input: OnGroupStoppedInput<
                        FilenameInput,
                        UploadDetectionsOutput
                    >,
                ): void => {
                    if (input.error.length > 0) {
                        addToast({
                            content:
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .UPLOAD_DETECTIONS.ERRORS
                                    .UPLOAD_DETECTION_FILE,
                            kind: 'warning-banner',
                        });
                    } else {
                        //  When upload is finished, check for successful output and use to update
                        //  the current `context.trqkGap`
                        const assetUrl =
                            input.success?.[0]?.output?.assetUrl ?? null;

                        const filename =
                            input.success?.[0]?.output?.filename ?? null;

                        const updatedTrqkGap =
                            input.success?.[0]?.output?.trqkGap ?? null;

                        if (assetUrl && filename && updatedTrqkGap) {
                            send({
                                type: 'SET_TRQK_GAP',
                                value: {
                                    trqkGap: updatedTrqkGap,
                                },
                            });

                            send({
                                type: 'SET_UPLOADED_DETECTION_FILE',
                                value: {
                                    uploadedDetectionFile: assetUrl,
                                },
                            });

                            send({
                                type: 'GO_TO_PREVIOUS_STEP',
                                value: {
                                    resetPreviousStepTitle: false,
                                },
                            });
                        } else {
                            addToast({
                                content:
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .UPLOAD_DETECTIONS.ERRORS
                                        .UPLOAD_DETECTION_FILE,
                                kind: 'warning-banner',
                            });
                        }
                    }
                };

                if (gapAnalysisId !== null) {
                    handleFiles({
                        files,
                        gapAnalysisId,
                        onGroupStopped,
                    });
                }
            }
        },
        [addToast, handleFiles, gapAnalysisId],
    );

    const [activeTabKey, setActiveTabKey] = useState(tabKeys.BMAT);

    const dateRanges = getGapDateRangesFromPeriodSelection(selectedPeriod);

    return (
        <FileDropZone
            addClasses="rounded-lg"
            acceptFileTypes={SUPPORTED_DETECTION_FILE_TYPES}
            DropZoneContent={DropZoneContent}
            handleDrop={({
                validFiles,
                invalidFiles,
            }: HandleDropInput): void => {
                uploadOrRejectFiles([...validFiles, ...invalidFiles]);
            }}
        >
            <StepWrapper context={context} remove={remove} send={send}>
                <div className="flex flex-col justify-start">
                    <div className="flex justify-between items-end mb-2">
                        <div className="text-sm w-2/3">
                            <p>
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .UPLOAD_DETECTIONS.DESCRIPTION
                                        .DRAG_DROP_OR_BROWSE
                                }
                            </p>
                            <p className="mt-2">
                                {
                                    locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                        .UPLOAD_DETECTIONS.DESCRIPTION
                                        .SINGLE_FILE_REQUIREMENT
                                }
                            </p>
                            <div
                                className="grid justify-items-start items-center"
                                style={{
                                    gridTemplateColumns: '20px auto',
                                }}
                            >
                                <IconInfoCircle
                                    fillColor="tertiary-400"
                                    width={16}
                                    addClasses="mt-2"
                                />
                                <p className="mt-2 text-tertiary-400 italic text-xs">
                                    {
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .UPLOAD_DETECTIONS.DESCRIPTION
                                            .CURRENTLY_SUPPORTED_FORMATS
                                    }
                                </p>
                            </div>
                        </div>
                        <Button
                            color="primary"
                            autoWidth={true}
                            addClasses="flex text-sm font-semibold items-center px-6 respond-to-mouse"
                            boxShadow={true}
                            handleClick={addFiles}
                        >
                            <IconMusicFile
                                fillColor="tertiary-200"
                                addClasses="mr-2"
                            />
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .UPLOAD_DETECTIONS.UPLOAD_CSV_FILE
                            }
                            <input
                                accept={fileInputAccept}
                                className="visually-hidden"
                                id="upload-detections-input"
                                onChange={(e: SyntheticEvent): void => {
                                    e.preventDefault();

                                    if (fileInputRef.current) {
                                        const files = Array.from(
                                            fileInputRef.current?.files ?? [],
                                        );

                                        uploadOrRejectFiles(files);

                                        // Reset the input's current value so it can be used again.
                                        fileInputRef.current.value = '';
                                    }
                                }}
                                ref={fileInputRef}
                                type="file"
                                multiple={false}
                            />
                        </Button>
                    </div>
                    <TabPanel
                        activeTabKey={activeTabKey}
                        setActiveTabKey={setActiveTabKey}
                        addClasses="px-12"
                        panels={[
                            {
                                key: tabKeys.BMAT,
                                label: locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .UPLOAD_DETECTIONS.UPLOAD_INSTRUCTIONS.BMAT
                                    .TAB_LABEL,
                                contents: (
                                    <BMATUploadInstructions
                                        performancePeriodEnd={
                                            dateRanges.performancePeriod.end
                                        }
                                        performancePeriodStart={
                                            dateRanges.performancePeriod.start
                                        }
                                    />
                                ),
                            },
                            {
                                key: tabKeys.TUNESAT,
                                label: locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .UPLOAD_DETECTIONS.UPLOAD_INSTRUCTIONS
                                    .TUNESAT.TAB_LABEL,
                                contents: (
                                    <TuneSatUploadInstructions
                                        performancePeriodEnd={
                                            dateRanges.performancePeriod.end
                                        }
                                        performancePeriodStart={
                                            dateRanges.performancePeriod.start
                                        }
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
            </StepWrapper>
        </FileDropZone>
    );
};
